<template>
  <section class="px-3 py-3 sm:px-5 sm:py-4 lg:px-8 lg:py-6">
    <div class="mx-auto max-w-7xl">
      <div class="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm sm:px-4 sm:py-4 lg:rounded-[1.75rem] lg:px-6 lg:py-6">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
          <div class="flex min-w-0 items-center gap-2.5 sm:gap-3 lg:gap-4">
            <HistoryBackButton />
            <img
              class="h-12 w-12 shrink-0 rounded-xl border border-slate-200 object-cover sm:h-14 sm:w-14 lg:h-20 lg:w-20 lg:rounded-[1.4rem]"
              :src="profileAvatar"
              :alt="displayName"
              loading="lazy"
            />

            <div class="min-w-0">
              <h1 class="truncate text-lg font-semibold text-slate-950 sm:text-xl lg:text-3xl">
                {{ displayName }}
              </h1>
              <p class="mt-0.5 truncate text-xs font-medium text-slate-500 sm:text-sm">
                {{ displayUsername }}
              </p>

              <div class="mt-3 hidden max-w-sm grid-cols-2 gap-2 lg:grid">
                <router-link
                  class="inline-flex min-h-9 min-w-0 items-center justify-center rounded-full bg-blue-600 px-3 text-center text-xs font-semibold text-white transition hover:bg-blue-700 sm:px-3.5"
                  to="/account/profile/edit"
                >
                  <span class="leading-none">Edit Profile</span>
                </router-link>

                <button
                  class="inline-flex min-h-9 min-w-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 px-3 text-center text-xs font-semibold text-blue-700 transition hover:bg-blue-100 sm:px-3.5"
                  type="button"
                  @click="shareProfile"
                >
                  <span class="leading-none">Share Profile</span>
                </button>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-2 sm:max-w-sm lg:hidden">
            <router-link
              class="inline-flex min-h-9 min-w-0 items-center justify-center rounded-full bg-blue-600 px-3 text-center text-xs font-semibold text-white transition hover:bg-blue-700 sm:px-3.5"
              to="/account/profile/edit"
            >
              <span class="leading-none">Edit Profile</span>
            </router-link>

            <button
              class="inline-flex min-h-9 min-w-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 px-3 text-center text-xs font-semibold text-blue-700 transition hover:bg-blue-100 sm:px-3.5"
              type="button"
              @click="shareProfile"
            >
              <span class="leading-none">Share Profile</span>
            </button>
          </div>

          <div class="grid grid-cols-3 items-stretch gap-1.5 sm:gap-2 lg:gap-3 lg:justify-end">
            <div
              v-for="item in statCards"
              :key="item.label"
              class="min-w-0 rounded-xl bg-slate-100 px-1.5 py-1.5 text-center sm:px-3 sm:py-2 lg:rounded-2xl lg:px-4 lg:py-3"
            >
              <div class="truncate text-[0.55rem] font-semibold uppercase tracking-[0.06em] text-slate-400 sm:text-[0.62rem] sm:tracking-[0.08em] lg:text-[11px] lg:tracking-[0.12em]">
                {{ item.label }}
              </div>
              <strong class="mt-0.5 block truncate text-sm font-semibold tabular-nums text-slate-950 sm:text-base lg:mt-1 lg:text-xl" :title="String(item.rawValue)">
                {{ item.value }}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 grid gap-6">
        <div class="rounded-[1.75rem] border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6 sm:py-6">
          <div class="flex justify-center border-b border-slate-200 pb-4">
            <div class="grid w-full grid-cols-4 gap-1 rounded-full bg-slate-100 p-1 lg:w-auto lg:min-w-[36rem]">
              <button
                v-for="tab in accountTabs"
                :key="tab.value"
                type="button"
                class="rounded-full px-2 py-2 text-center text-xs font-semibold transition sm:px-4 sm:text-sm"
                :class="activeAccountTab === tab.value ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900'"
                @click="switchAccountTab(tab.value)"
              >
                {{ tab.label }}
              </button>
            </div>
          </div>

          <template v-if="activeAccountTab === 'posted'">
            <div class="mt-5 hidden justify-end lg:flex">
              <span class="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                {{ flyers.length }} total
              </span>
            </div>

            <div v-if="isLoading" class="mt-5 grid gap-8">
              <section class="grid gap-4">
                <div class="flex items-center gap-3">
                  <span class="h-8 w-8 animate-pulse rounded-full bg-slate-100"></span>
                  <div class="grid gap-2">
                    <span class="h-4 w-28 animate-pulse rounded-full bg-slate-200"></span>
                    <span class="h-3 w-20 animate-pulse rounded-full bg-slate-100"></span>
                  </div>
                </div>
                <div class="account-flyer-masonry">
                  <div
                    v-for="(column, columnIndex) in skeletonFlyerColumns"
                    :key="`account-skeleton-column-${columnIndex}`"
                    class="account-flyer-masonry-column"
                  >
                    <FlyerCardSkeleton v-for="index in column" :key="`account-skeleton-${index}`" :index="index" />
                  </div>
                </div>
              </section>
            </div>

            <div v-else-if="!flyers.length" class="py-12 text-center">
              <div class="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-slate-100 text-slate-400">
                <svg class="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M6 3h9l3 3v15H6z" />
                  <path d="M14 3v4h4" />
                  <path d="M9 13h6" />
                  <path d="M9 17h4" />
                </svg>
              </div>
              <p class="m-0 mt-4 text-lg font-semibold text-slate-900">No flyers created</p>
              <p class="mt-2 text-sm font-medium text-slate-500">
                Create your first flyer and it will appear here.
              </p>
            </div>

            <div v-else class="mt-5 grid gap-8">
              <section v-for="group in groupedFlyers" :key="group.key" class="grid gap-4">
                <div class="sticky top-0 z-10 -mx-1 flex items-center gap-3 bg-white/95 px-1 py-1 backdrop-blur">
                  <span class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <rect x="3" y="4" width="18" height="18" rx="3" />
                      <path d="M8 2v4M16 2v4M3 10h18" />
                    </svg>
                  </span>
                  <div>
                    <h3 class="m-0 text-base font-semibold text-slate-950">{{ group.label }}</h3>
                    <p class="m-0 text-xs font-medium uppercase tracking-[0.1em] text-slate-400">
                      {{ group.items.length }} flyer{{ group.items.length === 1 ? '' : 's' }}
                    </p>
                  </div>
                </div>

                <div class="account-flyer-masonry">
                  <div
                    v-for="(column, columnIndex) in group.columns"
                    :key="`${group.key}-column-${columnIndex}`"
                    class="account-flyer-masonry-column"
                  >
                    <FlyerCard v-for="flyer in column" :key="flyer.id" :flyer="flyer" view="masonry" :show-author="false" />
                  </div>
                </div>
              </section>
            </div>
          </template>

          <template v-else-if="activeAccountTab === 'reviews'">
            <div class="mt-5 flex justify-end">
              <span class="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-amber-700">
                {{ reviews.length }} total
              </span>
            </div>

            <div v-if="reviewsLoading" class="account-flyer-masonry mt-5">
              <div
                v-for="(column, columnIndex) in skeletonFlyerColumns"
                :key="`review-skeleton-column-${columnIndex}`"
                class="account-flyer-masonry-column"
              >
                <FlyerCardSkeleton v-for="index in column" :key="`review-skeleton-${index}`" :index="index" />
              </div>
            </div>

            <div v-else-if="reviewsError" class="mt-5 rounded-2xl bg-red-50 px-4 py-4 text-center text-sm font-semibold text-red-700">
              <p class="m-0">{{ reviewsError }}</p>
              <button class="mt-3 rounded-full bg-white px-4 py-2 text-xs font-semibold text-red-700 shadow-sm" type="button" @click="loadReviews">
                Try again
              </button>
            </div>

            <div v-else-if="!reviews.length" class="py-12 text-center">
              <div class="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-amber-50 text-amber-500">
                <svg class="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z" />
                </svg>
              </div>
              <p class="m-0 mt-4 text-lg font-semibold text-slate-900">No reviews posted</p>
              <p class="mt-2 text-sm font-medium text-slate-500">Your business reviews will appear here.</p>
            </div>

            <div v-else class="account-flyer-masonry mt-5">
              <div
                v-for="(column, columnIndex) in reviewColumns"
                :key="`account-review-column-${columnIndex}`"
                class="account-flyer-masonry-column"
              >
                <FlyerCard v-for="review in column" :key="review.id" :flyer="review" view="masonry" :show-author="false" />
              </div>
            </div>
          </template>

          <template v-else-if="activeAccountTab === 'checkins'">
            <div class="mt-5 flex justify-end">
              <span class="rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">
                {{ checkins.length }} total
              </span>
            </div>

            <div v-if="checkinsLoading" class="account-flyer-masonry mt-5">
              <div
                v-for="(column, columnIndex) in skeletonFlyerColumns"
                :key="`checkin-skeleton-column-${columnIndex}`"
                class="account-flyer-masonry-column"
              >
                <div
                  v-for="index in column"
                  :key="`checkin-skeleton-${index}`"
                  class="grid animate-pulse gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                  aria-hidden="true"
                >
                  <div class="flex items-center gap-2.5 rounded-xl bg-slate-50 p-2.5">
                    <span class="h-9 w-9 shrink-0 rounded-xl bg-slate-200"></span>
                    <span class="grid min-w-0 flex-1 gap-2">
                      <span class="h-3.5 w-2/3 rounded-full bg-slate-200"></span>
                      <span class="h-3 w-5/6 rounded-full bg-slate-100"></span>
                    </span>
                  </div>
                  <span class="h-3.5 w-full rounded-full bg-slate-100"></span>
                  <span class="h-3.5 w-4/5 rounded-full bg-slate-100"></span>
                </div>
              </div>
            </div>

            <div v-else-if="checkinsError" class="mt-5 rounded-2xl bg-red-50 px-4 py-4 text-center text-sm font-semibold text-red-700">
              <p class="m-0">{{ checkinsError }}</p>
              <button class="mt-3 rounded-full bg-white px-4 py-2 text-xs font-semibold text-red-700 shadow-sm" type="button" @click="loadCheckins">
                Try again
              </button>
            </div>

            <div v-else-if="!checkins.length" class="py-12 text-center">
              <div class="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-blue-50 text-blue-500">
                <svg class="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M7 10v10" />
                  <path d="M4 10h3v10H4a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2Z" />
                  <path d="M7 18h9.1a2 2 0 0 0 1.9-1.4l1.7-5A2 2 0 0 0 17.8 9H14l.5-3.1A2.5 2.5 0 0 0 12 3l-1 3.5L7 10" />
                </svg>
              </div>
              <p class="m-0 mt-4 text-lg font-semibold text-slate-900">No checkins posted</p>
              <p class="mt-2 text-sm font-medium text-slate-500">Your business checkins will appear here.</p>
            </div>

            <div v-else class="account-flyer-masonry mt-5">
              <div
                v-for="(column, columnIndex) in checkinColumns"
                :key="`account-checkin-column-${columnIndex}`"
                class="account-flyer-masonry-column"
              >
                <FlyerCard
                  v-for="checkin in column"
                  :key="checkin.id"
                  :flyer="checkin"
                  view="masonry"
                  :show-author="false"
                  :business-post-clickable="false"
                  collapsible-comment
                />
              </div>
            </div>
          </template>

          <template v-else>
            <div v-if="!activeBoard" class="mt-5 flex justify-end">
              <button
                type="button"
                class="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                :disabled="boardsLoading"
                @click="openBoardCreator"
              >
                <i class="ri-add-line text-lg" aria-hidden="true"></i>
                <span>Create board</span>
              </button>
            </div>

            <div v-if="boardsLoading" class="py-12 text-center text-sm font-semibold text-slate-500">
              Loading boards...
            </div>

            <div v-else-if="boardsError" class="mt-5 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {{ boardsError }}
            </div>

            <template v-else-if="activeBoard">
              <div class="mt-5 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  class="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  @click="closeBoard"
                >
                  <i class="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
                  <span>Boards</span>
                </button>
                <div class="min-w-0 text-right">
                  <h3 class="m-0 truncate text-lg font-semibold text-slate-950">{{ activeBoard.name }}</h3>
                  <p class="m-0 mt-1 text-xs font-semibold uppercase tracking-[0.1em] text-slate-400">
                    {{ boardFlyers.length }} flyer{{ boardFlyers.length === 1 ? '' : 's' }}
                  </p>
                </div>
              </div>

              <div v-if="boardFlyersLoading" class="py-12 text-center text-sm font-semibold text-slate-500">
                Loading flyers...
              </div>

              <div v-else-if="boardFlyersError" class="mt-5 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                {{ boardFlyersError }}
              </div>

              <div v-else-if="!boardFlyers.length" class="py-12 text-center">
                <p class="m-0 text-lg font-semibold text-slate-900">No flyers in this board</p>
                <p class="mt-2 text-sm font-medium text-slate-500">Saved flyers for this board will appear here.</p>
              </div>

              <div v-else class="account-flyer-masonry mt-5">
                <div
                  v-for="(column, columnIndex) in boardFlyerColumns"
                  :key="`account-board-column-${columnIndex}`"
                  class="account-flyer-masonry-column"
                >
                  <FlyerCard v-for="flyer in column" :key="flyer.id" :flyer="flyer" view="masonry" />
                </div>
              </div>
            </template>

            <div v-else-if="!boards.length" class="py-12 text-center">
              <p class="m-0 text-lg font-semibold text-slate-900">No boards created</p>
              <p class="mt-2 text-sm font-medium text-slate-500">Saved flyer boards will appear here.</p>
            </div>

            <div v-else class="mt-5 grid gap-3">
              <article
                v-for="board in boards"
                :key="board.id"
                class="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-blue-200 hover:bg-blue-50/30"
                role="button"
                tabindex="0"
                @click="openBoard(board)"
                @keydown.enter="openBoard(board)"
                @keydown.space.prevent="openBoard(board)"
              >
                <div class="flex min-w-0 items-center gap-3">
                  <div class="min-w-0">
                    <div class="flex min-w-0 items-center gap-2">
                      <h3 class="m-0 truncate text-base font-semibold text-slate-950">{{ board.name }}</h3>
                      <button
                        type="button"
                        class="grid h-8 w-8 shrink-0 place-items-center rounded-full text-blue-600 transition hover:bg-blue-100"
                        aria-label="Edit board"
                        @click.stop="openBoardEditor(board)"
                      >
                        <i class="ri-pencil-line text-base" aria-hidden="true"></i>
                      </button>
                    </div>
                    <span class="mt-2 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                      {{ board.count }} {{ board.count === 1 ? 'flyer' : 'flyers' }}
                    </span>
                  </div>
                </div>

                <i class="ri-arrow-right-s-line shrink-0 text-2xl text-slate-400" aria-hidden="true"></i>
              </article>
            </div>
          </template>
        </div>

      </div>
    </div>
  </section>
  <Teleport to="body">
    <div
      v-if="profileShareModalOpen"
      class="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label="Share profile"
      @click.self="profileShareModalOpen = false"
    >
      <section class="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <h2 class="m-0 text-lg font-semibold text-slate-950">Share profile</h2>
            <p class="m-0 mt-1 text-xs font-medium text-slate-500">{{ displayUsername }}</p>
          </div>
          <button class="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-950" type="button" aria-label="Close share profile" @click="profileShareModalOpen = false">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div class="grid gap-4 p-4">
          <div class="grid place-items-center rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <img
              v-if="profileQrImage"
              class="h-48 w-48 rounded-xl bg-white object-contain p-2 shadow-sm"
              :src="profileQrImage"
              :alt="`${displayName} profile QR code`"
              loading="lazy"
            />
            <div v-else class="grid h-48 w-48 place-items-center rounded-xl border border-dashed border-slate-300 bg-white text-center text-sm font-semibold text-slate-400">
              QR code not available
            </div>
          </div>

          <div class="flex min-w-0 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-2">
            <span class="min-w-0 flex-1 truncate px-2 text-sm font-medium text-slate-700">{{ profileQrLink }}</span>
            <button
              class="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-blue-600 text-white transition hover:bg-blue-700"
              type="button"
              aria-label="Copy profile link"
              @click="copyProfileLink"
            >
              <i class="ri-file-copy-line text-lg" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>
    </div>
  </Teleport>
  <Teleport to="body">
    <div
      v-if="boardEditorOpen"
      class="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label="Edit board"
      @click.self="closeBoardEditor"
    >
      <form class="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl" @submit.prevent="submitBoardEdit">
        <header class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 class="m-0 text-lg font-semibold text-slate-950">Edit board</h2>
          <button class="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-950" type="button" aria-label="Close edit board" @click="closeBoardEditor">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div class="grid gap-3 p-4">
          <label class="grid gap-2 text-sm font-semibold text-slate-700">
            <span>Board name</span>
            <input
              v-model="boardEditName"
              class="min-h-11 rounded-2xl border border-slate-300 bg-white px-4 text-slate-900 outline-none transition focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
              type="text"
              placeholder="Board name"
              autocomplete="off"
            />
          </label>
          <p v-if="boardEditError" class="m-0 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
            {{ boardEditError }}
          </p>
        </div>

        <footer class="grid grid-cols-2 gap-2 border-t border-slate-200 px-4 py-3">
          <button class="inline-flex min-h-10 items-center justify-center rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50" type="button" :disabled="boardEditSaving" @click="closeBoardEditor">
            Cancel
          </button>
          <button class="inline-flex min-h-10 items-center justify-center rounded-full bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700" type="submit" :disabled="boardEditSaving">
            {{ boardEditSaving ? 'Saving...' : 'Save' }}
          </button>
        </footer>
      </form>
    </div>
  </Teleport>
  <Teleport to="body">
    <div
      v-if="boardCreatorOpen"
      class="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label="Create board"
      @click.self="closeBoardCreator"
    >
      <form class="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl" @submit.prevent="submitBoardCreate">
        <header class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 class="m-0 text-lg font-semibold text-slate-950">Create board</h2>
          <button class="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-950" type="button" aria-label="Close create board" @click="closeBoardCreator">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div class="grid gap-3 p-4">
          <label class="grid gap-2 text-sm font-semibold text-slate-700">
            <span>Board name</span>
            <input
              v-model="boardCreateName"
              class="min-h-11 rounded-2xl border border-slate-300 bg-white px-4 text-slate-900 outline-none transition focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
              type="text"
              placeholder="Board name"
              autocomplete="off"
            />
          </label>
          <p v-if="boardCreateError" class="m-0 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
            {{ boardCreateError }}
          </p>
        </div>

        <footer class="grid grid-cols-2 gap-2 border-t border-slate-200 px-4 py-3">
          <button class="inline-flex min-h-10 items-center justify-center rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50" type="button" :disabled="boardCreateSaving" @click="closeBoardCreator">
            Cancel
          </button>
          <button class="inline-flex min-h-10 items-center justify-center rounded-full bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700" type="submit" :disabled="boardCreateSaving">
            {{ boardCreateSaving ? 'Creating...' : 'Create' }}
          </button>
        </footer>
      </form>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import Swal from 'sweetalert2'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import FlyerCard from '@/components/common/FlyerCard.vue'
import FlyerCardSkeleton from '@/components/common/FlyerCardSkeleton.vue'
import api from '@/services/api'
import { formatFlyerListDate, parseApiDate } from '@/utils/date'
import { useSessionStore } from '@/stores/session'
import { getFlyerBoardId, normalizeFlyerSaved } from '@/utils/flyerDetail'

const session = useSessionStore()
const isLoading = ref(false)
const flyers = ref([])
const reviews = ref([])
const reviewsLoading = ref(false)
const reviewsError = ref('')
const reviewsLoaded = ref(false)
const checkins = ref([])
const checkinsLoading = ref(false)
const checkinsError = ref('')
const checkinsLoaded = ref(false)
const activeAccountTab = ref('posted')
const boards = ref([])
const boardsLoading = ref(false)
const boardsError = ref('')
const boardsLoaded = ref(false)
const activeBoard = ref(null)
const boardFlyers = ref([])
const boardFlyersLoading = ref(false)
const boardFlyersError = ref('')
const boardEditorOpen = ref(false)
const editingBoard = ref(null)
const boardEditName = ref('')
const boardEditError = ref('')
const boardEditSaving = ref(false)
const boardCreatorOpen = ref(false)
const boardCreateName = ref('')
const boardCreateError = ref('')
const boardCreateSaving = ref(false)
const masonryColumnCount = ref(2)
const profileShareModalOpen = ref(false)
const accountMeta = ref({
  followcount: 0,
  followingcount: 0,
  userinfo: null
})

const accountTabs = [
  { label: 'Posted', value: 'posted' },
  { label: 'Reviews', value: 'reviews' },
  { label: 'Checkins', value: 'checkins' },
  { label: 'Boards', value: 'boards' }
]

const displayName = computed(() => {
  const apiUser = accountMeta.value.userinfo || {}
  const profile = session.profile || {}
  const user = session.user || {}
  return apiUser.name || profile.name || profile.user_name || user.name || 'NetFlyr User'
})

const displayUsername = computed(() => {
  const apiUser = accountMeta.value.userinfo || {}
  const profile = session.profile || {}
  const user = session.user || {}
  const raw = apiUser.username || profile.username || profile.user_name || user.username || user.user_name || displayName.value
  return raw.startsWith('@') ? raw : `@${String(raw).replace(/^@/, '')}`
})

const profileAvatar = computed(() => {
  const apiUser = accountMeta.value.userinfo || {}
  const profile = session.profile || {}
  const user = session.user || {}
  const avatar = apiUser.user_img || profile.profile_pic || profile.image || user.profile_pic || user.image || '/assets/img/av.png'
  return avatar || '/assets/img/av.png'
})

const profileId = computed(() => {
  const apiUser = accountMeta.value.userinfo || {}
  const profile = session.profile || {}
  const user = session.user || {}
  return apiUser.id || apiUser.user_id || profile.id || profile.user_id || user.id || user.user_id || session.userId || ''
})

const profileShareUrl = computed(() => {
  if (typeof window === 'undefined') return `/users/${profileId.value}`
  return `${window.location.origin}/users/${profileId.value}`
})

const firstRealProfileLink = (...values) =>
  values.find((value) => {
    const normalized = String(value ?? '').trim()
    return normalized && normalized !== '0'
  }) || ''

const profileQrImage = computed(() => {
  const apiUser = accountMeta.value.userinfo || {}
  const profile = session.profile || {}
  const user = session.user || {}
  const image = profile.qrcode_image || profile.qr_code_image || apiUser.qrcode_image || apiUser.qr_code_image || user.qrcode_image || user.qr_code_image || ''
  return buildImageUrl(image)
})

const profileQrLink = computed(() => {
  const apiUser = accountMeta.value.userinfo || {}
  const profile = session.profile || {}
  const user = session.user || {}
  return firstRealProfileLink(
    profile.profile_link,
    profile.qrcode_link,
    profile.qr_code_link,
    apiUser.profile_link,
    apiUser.qrcode_link,
    apiUser.qr_code_link,
    user.profile_link,
    user.qrcode_link,
    user.qr_code_link,
    profileShareUrl.value
  )
})

const showCopyToast = (title) => {
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'success',
    title,
    showConfirmButton: false,
    timer: 1600,
    timerProgressBar: true
  })
}

const shareProfile = () => {
  if (!profileId.value) return
  profileShareModalOpen.value = true
}

const copyProfileLink = async () => {
  try {
    await navigator.clipboard.writeText(profileQrLink.value)
    showCopyToast('Profile link copied')
  } catch {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'error',
      title: 'Copy failed',
      showConfirmButton: false,
      timer: 1600,
      timerProgressBar: true
    })
  }
}

const formatStatCount = (value = 0) => {
  const count = Number(value || 0)
  if (!Number.isFinite(count)) return '0'
  if (count >= 1000000) return `${(count / 1000000).toFixed(count >= 10000000 ? 0 : 1).replace(/\.0$/, '')}M`
  if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1).replace(/\.0$/, '')}K`
  return String(count)
}

const makeStatCard = (label, rawValue) => ({
  label,
  rawValue: Number(rawValue || 0),
  value: formatStatCount(rawValue)
})

const statCards = computed(() => {
  const profile = session.profile || {}
  const user = session.user || {}
  const followers = accountMeta.value.followcount ||
    profile.followcount ||
    profile.followers_count ||
    profile.followcount_flyer ||
    user.followcount ||
    user.followers_count ||
    0
  const following = accountMeta.value.followingcount ||
    profile.followingcount ||
    profile.following_count ||
    profile.followingcount_flyer ||
    user.followingcount ||
    user.following_count ||
    0

  return [
    makeStatCard('Posts', flyers.value.length),
    makeStatCard('Followers', followers),
    makeStatCard('Following', following)
  ]
})

const switchAccountTab = (tab) => {
  activeAccountTab.value = tab
  if (tab === 'reviews' && !reviewsLoaded.value) {
    loadReviews()
  }
  if (tab === 'checkins' && !checkinsLoaded.value) {
    loadCheckins()
  }
  if (tab === 'boards' && !boardsLoaded.value) {
    loadBoards()
  }
}

const distributeByColumn = (items, columnCount) => {
  const count = Math.max(1, Number(columnCount) || 1)
  const columns = Array.from({ length: count }, () => [])
  items.forEach((item, index) => {
    columns[index % count].push(item)
  })
  return columns
}

const skeletonFlyerColumns = computed(() => distributeByColumn(Array.from({ length: 8 }, (_, index) => index + 1), masonryColumnCount.value))
const reviewColumns = computed(() => distributeByColumn(reviews.value, masonryColumnCount.value))
const checkinColumns = computed(() => distributeByColumn(checkins.value, masonryColumnCount.value))
const boardFlyerColumns = computed(() => distributeByColumn(boardFlyers.value, masonryColumnCount.value))

const updateMasonryColumnCount = () => {
  if (typeof window === 'undefined') return
  if (window.innerWidth >= 1280) {
    masonryColumnCount.value = 4
    return
  }
  masonryColumnCount.value = window.innerWidth >= 768 ? 3 : 2
}

const normalizeStatus = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized === '1' || normalized === 'active' || normalized === 'published' || normalized === 'true') {
    return 'Active'
  }
  if (normalized === '0' || normalized === 'inactive' || normalized === 'draft' || normalized === 'false') {
    return 'Inactive'
  }
  return 'Inactive'
}

const buildImageUrl = (value) => {
  const image = Array.isArray(value) ? value[0] : value
  if (!image) return ''
  if (/^https?:\/\//i.test(image)) return image
  return `https://test.amplifierapp.xyz/${String(image).replace(/^\/+/, '')}`
}

const normalizeBoardName = (value) => String(value ?? '').trim()

const getBoardApiId = (item = {}, { allowGenericId = true } = {}) =>
  item.board_id ??
  item.boardid ??
  item.boardId ??
  item.saved_board_id ??
  item.savedBoardId ??
  item.saved_id ??
  item.savedId ??
  (allowGenericId ? item.id : '') ??
  (allowGenericId ? item.value : '') ??
  ''

const normalizeBoard = (item = {}, index = 0) => {
  const boardApiId = getBoardApiId(item)

  return {
    id: String(boardApiId || `board-${index + 1}`),
    apiId: String(boardApiId || ''),
    name: normalizeBoardName(item.name ?? item.board_name ?? item.boardName ?? item.title ?? item.label ?? 'Saved Board'),
    description: String(item.description ?? item.desc ?? item.subtitle ?? '').trim(),
    count: Number(item.count ?? item.flyer_count ?? item.flyers_count ?? item.total_flyers ?? item.total ?? 0),
    flyers: Array.isArray(item.flyers) ? item.flyers : Array.isArray(item.items) ? item.items : []
  }
}

const boardFromGroup = ([name, flyerRows], index = 0, boardMeta = {}) => {
  const flyerList = Array.isArray(flyerRows) ? flyerRows : []
  const latestFlyer = flyerList[0] || {}
  const boardId = getBoardApiId(boardMeta) || getBoardApiId(latestFlyer, { allowGenericId: false }) || latestFlyer.board?.board_id || ''
  return {
    id: String(boardId || `board-${index}-${name}`),
    apiId: String(boardId || ''),
    name: normalizeBoardName(boardMeta.name ?? boardMeta.board_name ?? boardMeta.boardName ?? name),
    description: String(boardMeta.description ?? boardMeta.desc ?? boardMeta.subtitle ?? '').trim() || (latestFlyer.description ? `Latest: ${String(latestFlyer.description).slice(0, 80)}` : ''),
    count: Number(boardMeta.count ?? boardMeta.flyer_count ?? boardMeta.flyers_count ?? boardMeta.total_flyers ?? boardMeta.total ?? flyerList.length),
    flyers: flyerList
  }
}

const uniqueBoardsByName = (rows = []) => {
  const boardMap = new Map()

  rows.forEach((board, index) => {
    if (!board?.name) return
    const key = board.name.toLowerCase()
    const existing = boardMap.get(key)

    if (existing) {
      boardMap.set(key, {
        ...existing,
        apiId: existing.apiId || board.apiId,
        count: Math.max(existing.count, board.count || 0),
        description: existing.description || board.description,
        flyers: existing.flyers?.length ? existing.flyers : board.flyers
      })
      return
    }

    boardMap.set(key, { ...board, id: board.id || `board-${index + 1}` })
  })

  return Array.from(boardMap.values())
}

const extractBoardRows = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.boards,
    payload.saved_boards,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.boards,
    payload.data?.saved_boards
  ]

  const boardMetaByName = new Map()
  const boardMetaRows = [
    payload.boards,
    payload.data?.boards,
    payload.saved_boards,
    payload.data?.saved_boards
  ]
    .filter(Array.isArray)
    .flat()

  boardMetaRows.forEach((item = {}) => {
    const name = normalizeBoardName(item.name ?? item.board_name ?? item.boardName ?? item.title ?? item.label ?? '')
    if (name) boardMetaByName.set(name.toLowerCase(), item)
  })

  const keyedBoardGroups = candidates
    .filter((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate))
    .flatMap((candidate) =>
      Object.entries(candidate)
        .filter(([key, value]) => key !== 'boards' && Array.isArray(value))
        .map(([key, value], index) => boardFromGroup([key, value], index, boardMetaByName.get(normalizeBoardName(key).toLowerCase()) || {}))
    )

  const explicitBoardRows = candidates
    .filter(Array.isArray)
    .flatMap((candidate) => candidate.map(normalizeBoard))

  return uniqueBoardsByName([...keyedBoardGroups, ...explicitBoardRows])
}

const normalizeFlyer = (item = {}, index = 0) => {
  const timeAgo = String(item.time_ago || item.time_Ago || item.timeAgo || '').trim()
  const dateValue = item.posted_at || item.post_date || item.created_at || item.created_time || item.updated_at || item.created_date || item.date || ''
  const parsedDate = parseApiDate(dateValue)
  const listDate = formatFlyerListDate(parsedDate || dateValue)
  const normalizedImages = Array.from(new Set([
    ...(Array.isArray(item.images) ? item.images : []),
    item.image,
    item.image1,
    item.image2,
    item.image3,
    item.image4,
    item.flyer_image,
    item.image_url
  ].map(buildImageUrl).filter(Boolean)))
  const boardId = getFlyerBoardId(item)
  const apiUser = accountMeta.value.userinfo || {}
  const profile = session.profile || {}
  const user = session.user || {}
  const authorName = item.author || item.user_name || item.username || apiUser.name || apiUser.username || displayName.value
  const city = item.city || item.area || item.locality || ''
  const businessAddress = item.business_adress || item.business_address || item.businessAddress || item.s_address || ''
  const locationText = item.location || item.location_name || item.address || businessAddress || city || item.state || item.street || item.country_id || item.country || ''
  const rawCategory = item.category || item.content_type || item.category_id || ''
  const categoryName =
    item.category_name ||
    item.catergory_name ||
    item.cat_name ||
    item.content_type_name ||
    item.flyer_category ||
    item.category_title ||
    (rawCategory && Number.isNaN(Number(rawCategory)) ? rawCategory : '') ||
    'Flyer'

  return {
    id: item.id || item.flyer_id || `local-${index}`,
    title: item.title || item.name || item.flyer_title || '',
    category: categoryName,
    category_name: categoryName,
    contentTypeId: item.content_type || item.content_type_id || item.category_id || '',
    description: item.description || item.desc || item.caption || '',
    image: normalizedImages[0] || '',
    images: normalizedImages,
    param_code: item.param_code || item.paramCode || item.code || item.color_code || item.colorCode || item.color || '',
    code: item.code || item.param_code || item.paramCode || item.color_code || item.colorCode || item.color || '',
    accent: item.accent || item.categoryColor || '#64748b',
    address: item.address || item.flyer_address || item.location_name || item.location || businessAddress || city || '',
    city,
    area: item.area || '',
    locality: item.locality || '',
    state: item.state || '',
    street: item.street || '',
    country_id: item.country_id || '',
    country: item.country || '',
    location: locationText,
    status: normalizeStatus(item.status ?? item.publish_status),
    boardId,
    board_id: boardId,
    saved: normalizeFlyerSaved(item),
    userId: item.user_id || item.userid || session.userId,
    author: authorName,
    authorAvatar: buildImageUrl(item.user_img || item.profile_pic || item.user_image || item.profile_image || apiUser.user_img || profile.profile_pic || profile.image || user.user_img || user.profile_pic || user.image || ''),
    time_ago: timeAgo,
    time_Ago: timeAgo,
    postedAt: item.posted_at || item.post_date || item.created_at || item.created_time || item.updated_at || item.created_date || item.date || dateValue,
    date: timeAgo || listDate || '',
    parsedDate,
    displayDate: listDate || 'No date',
    groupKey: parsedDate ? parsedDate.toISOString().slice(0, 10) : `undated-${index}`,
    flyer_type: String(item.flyer_type || item.flyerType || '').toLowerCase(),
    review_count: Number(item.review_count || item.rating || 0),
    review_comment: item.review_comment || item.review || '',
    business_id: item.business_id || item.businessId || '',
    business_name: item.business_name || item.businessName || item.s_business || '',
    business_adress: businessAddress,
    business_lat: item.business_lat ?? item.businessLatitude ?? item.business_latitude ?? item.s_latit ?? '',
    business_long: item.business_long ?? item.businessLong ?? item.business_longitude ?? item.s_longit ?? '',
    checkin_date: item.checkin_date || ''
  }
}

const groupedFlyers = computed(() => {
  const groups = new Map()

  flyers.value.forEach((flyer) => {
    if (!groups.has(flyer.groupKey)) {
      groups.set(flyer.groupKey, {
        key: flyer.groupKey,
        label: flyer.displayDate,
        sortValue: flyer.parsedDate ? flyer.parsedDate.getTime() : 0,
        items: []
      })
    }

    groups.get(flyer.groupKey).items.push(flyer)
  })

  return Array.from(groups.values())
    .sort((a, b) => b.sortValue - a.sortValue)
    .map((group) => ({
      ...group,
      columns: distributeByColumn(group.items, masonryColumnCount.value)
    }))
})

const flattenDateBuckets = (payload) => {
  if (!payload || typeof payload !== 'object') return []
  return Object.entries(payload).flatMap(([dateKey, items = []]) =>
    Array.isArray(items)
      ? items.map((item) => ({
          ...item,
          created_date: item.created_date || dateKey
        }))
      : []
  )
}

const flattenPayloadRows = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.list)) return payload.list
  if (payload?.list && typeof payload.list === 'object') return flattenDateBuckets(payload.list)
  if (Array.isArray(payload?.saved_list)) return payload.saved_list
  if (payload?.saved_list && typeof payload.saved_list === 'object') return flattenDateBuckets(payload.saved_list)
  return []
}

const extractReviewRows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.reviews,
    payload.review_list,
    payload.business_reviews,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.reviews,
    payload.data?.review_list,
    payload.data?.business_reviews
  ]

  const rows = candidates.find(Array.isArray)
  if (rows) return rows

  const groupedRows = candidates.find((candidate) => candidate && typeof candidate === 'object')
  return groupedRows ? flattenDateBuckets(groupedRows) : []
}

const extractCheckinRows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.checkins,
    payload.checkin_list,
    payload.business_checkins,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.checkins,
    payload.data?.checkin_list,
    payload.data?.business_checkins
  ]

  const rows = candidates.find(Array.isArray)
  if (rows) return rows

  const groupedRows = candidates.find((candidate) => candidate && typeof candidate === 'object')
  return groupedRows ? flattenDateBuckets(groupedRows) : []
}

const normalizeCheckin = (item = {}, index = 0) => normalizeFlyer({
  ...item,
  id: item.id || item.checkin_id || `checkin-${item.business_id || 'business'}-${item.checkin_date || index}-${index}`,
  flyer_type: 'checkin',
  review_comment: item.comments ?? item.review_comment ?? '',
  created_at: item.checkin_date || item.created_at || '',
  business_name: item.s_business || item.business_name || '',
  business_adress: item.s_address || item.business_adress || item.business_address || '',
  business_lat: item.s_latit ?? item.business_lat ?? '',
  business_long: item.s_longit ?? item.business_long ?? '',
  business_id: item.business_id || '',
  checkin_date: item.checkin_date || '',
  comments: item.comments ?? '',
  user_id: item.user_id || session.userId || session.user?.user_id || ''
}, `checkin-${index}`)

const loadProfile = async () => {
  if (!session.userId) return
  await session.fetchProfile()
}

const loadFlyers = async () => {
  if (!session.userId) {
    flyers.value = []
    reviews.value = []
    checkins.value = []
    return
  }

  isLoading.value = true

  try {
    const { data } = await api.listAddedFlyerLive({
      page: 1,
      user_id: session.userId,
      login_user_id: session.userId
    })

    accountMeta.value = {
      followcount: Number(data?.followcount || data?.followers_count || data?.userinfo?.followcount || data?.userinfo?.followers_count || data?.userinfo?.followcount_flyer || 0),
      followingcount: Number(data?.followingcount || data?.following_count || data?.userinfo?.followingcount || data?.userinfo?.following_count || data?.userinfo?.followingcount_flyer || 0),
      userinfo: data?.userinfo || null
    }

    const rows = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.list)
        ? data.list
        : flattenDateBuckets(data?.list)

    const normalizedRows = rows
      .map(normalizeFlyer)
      .sort((a, b) => (b.parsedDate?.getTime?.() || 0) - (a.parsedDate?.getTime?.() || 0))

    const isReviewFlyer = (flyer) => flyer.flyer_type === 'review' || Boolean(flyer.business_name || flyer.review_comment)
    flyers.value = normalizedRows.filter((flyer) => !isReviewFlyer(flyer))
  } catch {
    flyers.value = []
  } finally {
    isLoading.value = false
  }
}

const loadReviews = async () => {
  const userId = session.userId || session.user?.user_id || ''
  if (!userId) {
    reviews.value = []
    reviewsError.value = 'Reviews could not be loaded.'
    return
  }

  reviewsLoading.value = true
  reviewsError.value = ''

  try {
    const { data } = await api.listReviewBusiness({ user_id: userId })
    reviews.value = extractReviewRows(data)
      .map((item, index) => normalizeFlyer({ ...item, flyer_type: 'review' }, `review-${index}`))
      .sort((a, b) => (b.parsedDate?.getTime?.() || 0) - (a.parsedDate?.getTime?.() || 0))
    reviewsLoaded.value = true
  } catch (error) {
    reviews.value = []
    reviewsError.value = error?.response?.data?.message || 'Reviews could not be loaded.'
  } finally {
    reviewsLoading.value = false
  }
}

const loadCheckins = async () => {
  const userId = session.userId || session.user?.user_id || ''
  if (!userId) {
    checkins.value = []
    checkinsError.value = 'Checkins could not be loaded.'
    return
  }

  checkinsLoading.value = true
  checkinsError.value = ''

  try {
    const { data } = await api.listCheckins({ user_id: userId })
    checkins.value = extractCheckinRows(data)
      .map(normalizeCheckin)
      .sort((a, b) => (b.parsedDate?.getTime?.() || 0) - (a.parsedDate?.getTime?.() || 0))
    checkinsLoaded.value = true
  } catch (error) {
    checkins.value = []
    checkinsError.value = error?.response?.data?.message || 'Checkins could not be loaded.'
  } finally {
    checkinsLoading.value = false
  }
}

const loadBoards = async () => {
  const userId = session.userId || session.user?.user_id || ''
  if (!userId) {
    boards.value = []
    boardsLoaded.value = true
    return
  }

  boardsLoading.value = true
  boardsError.value = ''

  try {
    const { data } = await api.listBoardsFlyer({ user_id: userId })
    boards.value = extractBoardRows(data).filter((board) => board.name)
    boardsLoaded.value = true
  } catch (error) {
    boards.value = []
    boardsError.value = error?.response?.data?.message || 'Boards could not be loaded.'
  } finally {
    boardsLoading.value = false
  }
}

const loadBoardFlyers = async (board) => {
  const userId = session.userId || session.user?.user_id || ''
  const boardId = board?.apiId || board?.id || ''
  if (!userId || !boardId) {
    boardFlyers.value = []
    boardFlyersError.value = 'Board flyers could not be loaded.'
    return
  }

  boardFlyersLoading.value = true
  boardFlyersError.value = ''
  boardFlyers.value = Array.isArray(board.flyers) ? board.flyers.map(normalizeFlyer) : []

  try {
    const { data } = await api.savedFlyersUser({
      user_id: userId,
      page: 1,
      board_id: boardId
    })
    const rows = flattenPayloadRows(data)
    const normalizedRows = rows
      .map((item, index) => normalizeFlyer(item, `board-${index}`))
      .sort((a, b) => (b.parsedDate?.getTime?.() || 0) - (a.parsedDate?.getTime?.() || 0))
    boardFlyers.value = normalizedRows.length ? normalizedRows : boardFlyers.value
  } catch (error) {
    boardFlyers.value = []
    boardFlyersError.value = error?.response?.data?.message || 'Board flyers could not be loaded.'
  } finally {
    boardFlyersLoading.value = false
  }
}

const openBoard = (board) => {
  activeBoard.value = board
  loadBoardFlyers(board)
}

const closeBoard = () => {
  activeBoard.value = null
  boardFlyers.value = []
  boardFlyersError.value = ''
}

const openBoardCreator = () => {
  boardCreateName.value = ''
  boardCreateError.value = ''
  boardCreatorOpen.value = true
}

const closeBoardCreator = (force = false) => {
  if (boardCreateSaving.value && !force) return
  boardCreatorOpen.value = false
  boardCreateName.value = ''
  boardCreateError.value = ''
}

const submitBoardCreate = async () => {
  const name = boardCreateName.value.trim()
  const userId = session.userId || session.user?.user_id || ''

  if (!name) {
    boardCreateError.value = 'Enter board name.'
    return
  }

  if (!userId) {
    boardCreateError.value = 'Board could not be created.'
    return
  }

  boardCreateSaving.value = true
  boardCreateError.value = ''

  try {
    await api.addBoardFlyer({ user_id: userId, name })
    showCopyToast('Board created')
    closeBoardCreator(true)
    boardsLoaded.value = false
    await loadBoards()
  } catch (error) {
    boardCreateError.value = error?.response?.data?.message || 'Board could not be created.'
  } finally {
    boardCreateSaving.value = false
  }
}

const openBoardEditor = (board) => {
  editingBoard.value = board
  boardEditName.value = board?.name || ''
  boardEditError.value = ''
  boardEditorOpen.value = true
}

const closeBoardEditor = (force = false) => {
  if (boardEditSaving.value && !force) return
  boardEditorOpen.value = false
  editingBoard.value = null
  boardEditName.value = ''
  boardEditError.value = ''
}

const submitBoardEdit = async () => {
  const name = boardEditName.value.trim()
  const board = editingBoard.value || {}
  const boardId = board.apiId || ''

  if (!name) {
    boardEditError.value = 'Enter board name.'
    return
  }

  if (!boardId) {
    boardEditError.value = 'Board id is missing.'
    return
  }

  boardEditSaving.value = true
  boardEditError.value = ''

  try {
    await api.updateBoardFlyer({ id: boardId, name })
    showCopyToast('Board updated')
    closeBoardEditor(true)
    boardsLoaded.value = false
    await loadBoards()
  } catch (error) {
    boardEditError.value = error?.response?.data?.message || 'Board could not be updated.'
  } finally {
    boardEditSaving.value = false
  }
}

onMounted(async () => {
  updateMasonryColumnCount()
  window.addEventListener('resize', updateMasonryColumnCount)
  await loadProfile()
  await loadFlyers()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMasonryColumnCount)
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  -webkit-line-clamp: 2;
}

.account-flyer-masonry {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  align-items: start;
}

.account-flyer-masonry-column {
  display: grid;
  min-width: 0;
  gap: 0.85rem;
  align-content: start;
}

.account-flyer-masonry-column :deep(.flyer-card),
.account-flyer-masonry-column :deep(.flyer-card-skeleton) {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .account-flyer-masonry {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .account-flyer-masonry {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
